import { useCallback, useEffect, useRef, useState } from 'react';

// Configuration
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_LOGOUT_MS = 5 * 60 * 1000; // 5 minutes before logout
const ACTIVITY_THROTTLE_MS = 1000; // 1 second throttle

// Time until warning appears
const WARNING_TIMER_MS = INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS; // 25 minutes

interface UseInactivityTimeoutOptions {
  enabled?: boolean;
  onLogout: () => void | Promise<void>;
}

interface UseInactivityTimeoutReturn {
  showWarning: boolean;
  remainingSeconds: number;
  resetTimers: () => void;
  logout: () => void;
}

/**
 * Inactivity Timeout Hook
 * 
 * Tracks user activity and manages automatic logout after inactivity.
 * - Shows warning dialog 5 minutes before logout
 * - Automatically logs out after 30 minutes of inactivity
 * - Activity events: mousemove, mousedown, keydown, scroll, touchstart
 * - Throttled to 1 event per second to prevent excessive timer resets
 */
export const useInactivityTimeout = ({
  enabled = true,
  onLogout,
}: UseInactivityTimeoutOptions): UseInactivityTimeoutReturn => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_BEFORE_LOGOUT_MS / 1000);

  // Refs for timers
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs to avoid stale closures
  const lastActivityRef = useRef<number>(Date.now());
  const isLoggingOutRef = useRef(false);
  const onLogoutRef = useRef(onLogout);
  
  // Keep onLogout ref up to date
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    clearAllTimers();
    setShowWarning(false);
    
    try {
      await onLogoutRef.current();
    } catch (error) {
      console.error('[InactivityTimeout] Logout error:', error);
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [clearAllTimers]);

  // Start countdown when warning is shown
  const startCountdown = useCallback(() => {
    const logoutTime = Date.now() + WARNING_BEFORE_LOGOUT_MS;
    
    // Update remaining seconds every second
    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((logoutTime - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      
      if (remaining <= 0) {
        logout();
      }
    }, 1000);
  }, [logout]);

  // Show warning and start countdown
  const triggerWarning = useCallback(() => {
    setShowWarning(true);
    setRemainingSeconds(WARNING_BEFORE_LOGOUT_MS / 1000);
    startCountdown();
    
    // Set logout timer as backup
    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, WARNING_BEFORE_LOGOUT_MS);
  }, [startCountdown, logout]);

  // Reset all timers (called on activity or "Stay Logged In")
  const resetTimers = useCallback(() => {
    if (!enabled) return;
    
    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(WARNING_BEFORE_LOGOUT_MS / 1000);
    lastActivityRef.current = Date.now();
    
    // Start warning timer
    warningTimerRef.current = setTimeout(() => {
      triggerWarning();
    }, WARNING_TIMER_MS);
  }, [enabled, clearAllTimers, triggerWarning]);

  // Handle activity events (throttled)
  const handleActivity = useCallback(() => {
    if (!enabled || showWarning) return;
    
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {
      return; // Throttle
    }
    
    lastActivityRef.current = now;
    resetTimers();
  }, [enabled, showWarning, resetTimers]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    // Start initial timer
    resetTimers();

    // Activity events to track
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, handleActivity, resetTimers, clearAllTimers]);

  return {
    showWarning,
    remainingSeconds,
    resetTimers,
    logout,
  };
};
